import { ApiError as ApiErrorSchema, ContactlessUploadResult } from '@cloudsign/shared';
import type { z } from 'zod';
import type { SignatureValue } from '../../components/phase3';
import { ApiClientError } from '../../lib/api';
import { t } from '../../lib/i18n';

/**
 * API calls of the contactless pages (`/k/` and `/s/`, ADR 0022). Three kinds of call:
 *  - public ones carry the signed link token in the path and nothing else;
 *  - session ones carry `Authorization: Contactless <token>` (the lib/api client has no custom headers, so this page has its own);
 *  - the "remember me" token is kept in localStorage only when the visitor ticks the box.
 * A 401 never sends the visitor to the portal sign-in page: it just means this session ended.
 */

export type LinkKind = 'k' | 's';
const API = '/api/v1';

/** Public routes of each link kind (contactless site link or sign-in point link). */
export const publicBase = (kind: LinkKind): string => (kind === 's' ? '/sign-in-points/public' : '/contactless/public');

export interface CallOptions<S extends z.ZodType | undefined> {
  body?: unknown;
  query?: Record<string, string>;
  schema?: S;
  /** The session token; sent as `Authorization: Contactless <token>`. */
  session?: string | null;
}

type Out<S> = S extends z.ZodType ? z.infer<S> : undefined;

export async function call<S extends z.ZodType | undefined = undefined>(method: 'GET' | 'POST', path: string, opts: CallOptions<S> = {}): Promise<Out<S>> {
  const q = opts.query ? `?${new URLSearchParams(opts.query).toString()}` : '';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.session) headers['Authorization'] = `Contactless ${opts.session}`;
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(`${API}${path}${q}`, { method, headers, ...(body !== undefined ? { body } : {}), credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'no-store' });
  } catch {
    throw new ApiClientError(0, { code: 'internal', messageKey: 'error.network' });
  }
  if (res.status === 204 || res.status === 202) return undefined as Out<S>;
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const parsed = ApiErrorSchema.safeParse(json);
    throw new ApiClientError(res.status, parsed.success ? parsed.data.error : { code: 'internal', messageKey: 'error.internal' });
  }
  if (opts.schema && import.meta.env.DEV) return opts.schema.parse(json) as Out<S>;
  return json as Out<S>;
}

/** 404 or 410 on a public link: the QR was replaced or is too old. */
export function isLinkInvalid(e: unknown): boolean {
  return e instanceof ApiClientError && (e.status === 404 || e.status === 410 || e.messageKey === 'error.link_invalid');
}
/** 401 on a session call: the 30 minutes ran out (or the token was never valid). */
export function isSessionEnded(e: unknown): boolean {
  return e instanceof ApiClientError && e.status === 401;
}

/** Plain-words text for a failed call. Fields are shown by key (validation answers). */
export function problemText(e: unknown): string {
  if (e instanceof ApiClientError) {
    if (e.status === 429) return t('w21.rateLimited');
    if (e.fields) return Object.values(e.fields).map((k) => (k.startsWith('error.') ? t(k) : k)).join(' ');
    return t(e.messageKey, e.params);
  }
  return t('error.internal');
}

// ---------- Browser storage (always wrapped: private windows and blocked storage must not break the page) ----------

const REMEMBER_KEY = 'cs.contactless.remember';
const sessionKey = (kind: LinkKind, linkToken: string): string => `cs.contactless.session.${kind}.${linkToken.slice(-24)}`;

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export const rememberStore = {
  get: (): string | null => safe(() => localStorage.getItem(REMEMBER_KEY), null),
  set: (token: string): void => safe(() => localStorage.setItem(REMEMBER_KEY, token), undefined),
  clear: (): void => safe(() => localStorage.removeItem(REMEMBER_KEY), undefined),
};

export const sessionStore = {
  get: (kind: LinkKind, linkToken: string): string | null => safe(() => sessionStorage.getItem(sessionKey(kind, linkToken)), null),
  set: (kind: LinkKind, linkToken: string, token: string): void => safe(() => sessionStorage.setItem(sessionKey(kind, linkToken), token), undefined),
  clear: (kind: LinkKind, linkToken: string): void => safe(() => sessionStorage.removeItem(sessionKey(kind, linkToken)), undefined),
};

// ---------- Uploads from the phone (photo, thumbnail, signature) ----------

function toBase64(bytes: Uint8Array): string {
  let s = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) s += String.fromCharCode(...bytes.subarray(i, i + step));
  return btoa(s);
}

/** POST /visits/contactless/uploads (JSON form: the server accepts base64 as well as multipart). Returns the upload id to put in the step answers. */
export async function uploadFile(session: string, kind: 'photo' | 'photo_thumb' | 'signature', data: Blob | string): Promise<string> {
  const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : new Uint8Array(await data.arrayBuffer());
  const r = await call('POST', '/visits/contactless/uploads', { session, body: { kind, data: toBase64(bytes) }, schema: ContactlessUploadResult });
  return r.uploadId;
}

export const uploadSignature = (session: string, value: SignatureValue): Promise<string> => uploadFile(session, 'signature', JSON.stringify(value));

/** A typed name instead of a drawn signature: the vector file is empty and the name travels beside it. */
export const TYPED_SIGNATURE: SignatureValue = { v: 1, width: 480, height: 200, strokes: [] };
